import { faImage } from "@fortawesome/pro-solid-svg-icons"
import { Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { formatDate } from "app/util/date/tools/common"
import { CreativeDetailsPopup } from "form/components/content/creative/CreativeDetailsPopup"
import { IFormCreativeRow } from "form/model"
import { getSummaryCreativeRows } from "form/tools/creative"
import { CreativeStatus } from "media/components/CreativeStatus"
import { MediaType } from "media/components/MediaType"
import { IMedia } from "media/model"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { ListActionButton } from "ui/components/main-layout/ListActionButton"
import { getDataGridTexts } from "ui/tools/list"

interface ICampaignCreativeSummaryProps {
  medias: IMedia[]
  contentWidth: number
}

export const CampaignCreativeSummary = React.memo(({ medias, contentWidth }: ICampaignCreativeSummaryProps) => {
  const [isDetailsOpened, setIsDetailsOpened] = React.useState(false)
  const [selectedRow, setSelectedRow] = React.useState<IFormCreativeRow | null>(null)
  const rows = getSummaryCreativeRows(medias)
  if (rows.length === 0) {
    return (
      <Typography id="summary-campaign-creative-empty" color="textSecondary" fontSize=".85rem" sx={{ ml: "1rem" }}>
        {translate("campaign.creative.empty")}
      </Typography>
    )
  }
  if (contentWidth < 720) {
    return (
      <Typography id="summary-campaign-creative-count" color="textPrimary" fontSize=".85rem" sx={{ ml: "1rem" }}>
        {translate("label.creatives.count", rows.length)}
      </Typography>
    )
  }
  const columns: Array<GridColDef<IFormCreativeRow>> = [
    {
      field: "name",
      headerName: translate("label.name"),
      flex: 3
    },
    {
      field: "createdAt",
      headerName: translate("label.creationDate"),
      flex: 2,
      renderCell: params => formatDate(params.row.createdAt || "")
    },
    {
      field: "approval_status_id",
      headerName: translate("label.status"),
      flex: 2,
      renderCell: params => (
        <CreativeStatus status={params.row.approval_status_id} refusalNote={params.row.refusal_note} />
      )
    },
    {
      field: "type",
      headerName: translate("label.type"),
      flex: 1,
      renderCell: params => <MediaType type={params.row.type} />
    },
    {
      field: "action",
      headerName: "",
      flex: 0,
      align: "right",
      maxWidth: 16,
      sortable: false,
      renderCell: params =>
        params.row.type === "archive" ? null : (
          <ListActionButton
            tooltip={translate("campaign.creative.show.details")}
            icon={faImage}
            disabled={isDetailsOpened}
            bigSize
            onClick={event => {
              setSelectedRow(params.row)
              setIsDetailsOpened(true)
            }}
            onDisabledClick={event => setSelectedRow(params.row)}
          />
        )
    }
  ]
  return (
    <FlexBox
      id="summary-campaign-creative-list"
      vertical
      fullWidth
      sx={{
        "minWidth": "100%",
        "maxWidth": "100%",
        "height": "15rem",
        "minHeight": "15rem",
        "maxHeight": "15rem",
        "transition": "all 400ms ease",
        "& > *": {
          width: "100%"
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: "hidden"
        },
        "& .MuiDataGrid-root": {
          border: "none"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
          {
            outline: "none !important"
          }
      }}>
      <DataGrid
        rows={rows}
        columns={columns}
        disableColumnFilter
        disableColumnMenu
        className="creative-summary-list"
        rowHeight={30}
        autoPageSize
        localeText={getDataGridTexts()}
      />
      {isDetailsOpened && selectedRow && (
        <CreativeDetailsPopup
          row={selectedRow}
          onClose={() => {
            setSelectedRow(null)
            setIsDetailsOpened(false)
          }}
        />
      )}
    </FlexBox>
  )
})
